/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { retry, timeout } from 'rxjs/operators';
import { Constants } from '../../common/constants';
import { UserService } from '../services/user.service';
import { CloudRepo } from '../services/workflow.service';
import { XaccBrowseModalComponent } from './xacc-browse-modal.component';
import { MWDisplayItem } from './xacc-browse.component';

@Injectable({
  providedIn: 'root',
})
export class XaccService {
  private baseUrl = 'api/xacc/';
  private _connector = '';
  get connector(): string {
    return this._connector;
  }
  set connector(value: string) {
    this._connector = value;
    this.connectorDisplayName = this.getConnectorDisplayName(); // relies on region to be set before connector
  }

  public region = '';
  public connectorDisplayName = '';
  public connectorToken = '';
  public connectorRefreshToken = '';
  public connectorAccessListId = '';
  public repoOptions: any;
  public showBrowser = true;

  directoryOrFilesSelected: EventEmitter<any>;
  xaccLoginSucceed: EventEmitter<any>;

  private lastLoginUrl = '';
  private loginWindow: Window | null = null;
  private enableFileTypes = '';
  private isMultipleSelection = false;
  private maxSelection = 0;

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private userService: UserService,
  ) {
    this.directoryOrFilesSelected = new EventEmitter();
    this.xaccLoginSucceed = new EventEmitter();
  }

  public async initiateWebLogin(
    repo: CloudRepo,
    returning = false,
    enableFileTypes = '',
    isMultipleSelection = false,
    maxSelection = 0,
  ) {
    let attempts = 0;
    this.region = repo.region || '';
    this.connector = repo.connector || '';
    this.enableFileTypes = enableFileTypes;
    this.isMultipleSelection = isMultipleSelection;
    this.maxSelection = maxSelection;

    while (attempts < 2) {
      attempts++;
      try {
        const loginUrl = await this.http
          .get<string>(`${this.baseUrl}GetWebLogin`, {
            params: {
              UserAgent: window.navigator.userAgent,
              connector: this.connector,
              region: this.region,
              accessListId: this.userService.user?.accessListId || '',
            },
          })
          .pipe(timeout(5000), retry(3))
          .toPromise();
        if (loginUrl) {
          if (!returning) {
            this.repoOptions = null;
          }
          return this.openLoginWindow(loginUrl);
        }
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
  }

  public async returningWebLogin(
    returnRepo: CloudRepo,
    enableFileTypes = '',
    isMultipleSelection = false,
  ) {
    let attempts = 0;
    this.region = returnRepo.region || '';
    this.connector = returnRepo.connector || '';
    this.connectorToken = returnRepo.access || '';
    this.connectorRefreshToken = returnRepo.refresh || '';
    this.enableFileTypes = enableFileTypes;
    this.isMultipleSelection = isMultipleSelection;

    while (attempts < 2) {
      attempts++;
      try {
        const loginUrl = await this.http
          .get<string>(`${this.baseUrl}GetWebLogin`, {
            params: {
              UserAgent: window.navigator.userAgent,
              connector: this.connector,
              region: this.region,
              accessListId: this.userService.user?.accessListId || '',
            },
          })
          .toPromise();
        if (loginUrl) {
          // use the returning dir and path for ValidatePath() call
          this.repoOptions = {
            dir: returnRepo.dir,
            sitePath: returnRepo.sitePath,
          };
          return this.openLoginWindow(loginUrl);
        }
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
  }

  // TODO Remove when we pull all of the MFD code
  public async initiateLogin(repo: CloudRepo, returning: boolean) {
    let attempts = 0;
    this.region = repo.region || '';
    this.connector = repo.connector || '';

    while (attempts < 2) {
      attempts++;
      try {
        const loginUrl = await this.http
          .get<string>(`${this.baseUrl}GetLogin`, {
            params: {
              Model: encodeURIComponent('this.eipApiService.deviceInfo.model'),
              EipVersion: encodeURIComponent(
                'this.eipApiService.deviceInfo.eipVersion',
              ),
              SoftwareVersion: encodeURIComponent(
                'this.eipApiService.deviceInfo.firmwareVersion',
              ),
              UserAgent: encodeURIComponent(window.navigator.userAgent),
              connector: encodeURIComponent(this.connector),
              region: encodeURIComponent(this.region),
              accessListId: encodeURIComponent(
                this.userService.user?.accessListId || '',
              ),
            },
          })
          .toPromise();
        if (loginUrl) {
          if (!returning) {
            this.repoOptions = null;
          }
          window.location.href = encodeURI(loginUrl);
        }
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
  }

  public setRepo(returningRepo: MWReturningRepo) {
    this.region = returningRepo.region;
    this.connector = returningRepo.connector;
    this.connectorToken = returningRepo.accessToken;
    this.connectorRefreshToken = returningRepo.refreshToken;
  }

  //Throws exception if token is missing
  validateConnectorToken() {
    if (!this.connectorToken) {
      throw new Error('ACCESS_TOKEN_REQUIRED');
    }
  }

  public async validateRepoToken(repo: MWCloudRepo): Promise<void> {
    if (!repo.access) {
      throw new Error('ACCESS_TOKEN_REQUIRED');
    }

    try {
      await this.http
        .get<string>(`${this.baseUrl}ValidateToken`, {
          params: {
            token: repo.access,
            connector: repo.connector || '',
            region: repo.region || '',
            accessListId: this.userService.user?.accessListId || '',
          },
        })
        .toPromise();
      return;
    } catch (e) {
      throw e;
    }
  }

  //Throws exception if token is invalid, or folder does not exist
  async validatePath(repoOptions: MWCloudRepo): Promise<boolean> {
    let site: MWDisplayItem | null = null,
      library: MWDisplayItem | null = null;
    if (repoOptions.sitePath) {
      for (let i = repoOptions.sitePath.length - 1; i > 0; i--) {
        if (!site && repoOptions.sitePath[i].type === 'site') {
          site = repoOptions.sitePath[i];
        } else if (!library && repoOptions.sitePath[i].type === 'lib') {
          library = repoOptions.sitePath[i];
        }
      }
    }

    let libId = '',
      folderId = '';
    if (repoOptions?.dir?.type === 'lib') {
      libId = repoOptions.dir.id;
    } else {
      libId = library?.id || '';
      folderId = repoOptions.dir?.id || '';
    }

    this.connector = repoOptions.connector || this.connector;
    this.region = repoOptions.region || this.region;
    this.connectorToken = repoOptions.access || this.connectorToken;

    try {
      const folderContents = await this.browseById(
        site?.id || '',
        libId,
        folderId,
      );
    } catch (e) {
      if ((e as { message: string }).message !== undefined) {
        const errorMessage = (e as { message: string }).message;
        if (errorMessage == 'ACCESS_TOKEN_REQUIRED') {
          throw e;
        } else {
          throw new Error('BROWSE_FOLDER_REQUIRED');
        }
      }
    }

    return false;
  }

  public async browseById(
    siteId: string,
    libraryId: string,
    folderId: string,
    query = '',
    pageSize = 100,
    pageToken = '',
  ) {
    let result: RepoItemList | null = null;
    let attempts = 0;
    this.validateConnectorToken();

    // make sure pageToken != null
    const pToken = pageToken ? pageToken : '';

    while (attempts < 2) {
      attempts++;
      try {
        const rList = await this.http
          .get<RepoItemList>(`${this.baseUrl}BrowseById`, {
            params: {
              token: this.connectorToken,
              accessListId: this.userService.user?.accessListId || '',
              connector: this.connector,
              region: this.region,
              siteId: siteId || '',
              libraryId: libraryId || '',
              folderId: folderId || '',
              pageSize: pageSize,
              pageToken: pToken,
              query: query,
            },
          })
          .toPromise();
        if (rList) {
          result = rList;
        }
        attempts = 2;
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
    return result;
  }

  public async getProfile() {
    try {
      const profile = await this.http
        .get<MSGraphProfile>(`${this.baseUrl}Profile`, {
          params: {
            token: this.connectorToken,
            accessListId: this.userService.user?.accessListId || '',
          },
        })
        .toPromise();
      if (profile) {
        return profile;
      }
    } catch (error) {
      console.log('Failed in get MSGraphProfile');
      throw error;
    }
    return null;
  }

  private getConnectorDisplayName(): string {
    let name = '';
    const midDest = Constants.middlewareDestinations.find(
      (x) => x.value === this.connector && x.region == this.region,
    );
    name = (midDest && midDest.key) || this.connector;
    return name;
  }

  private async refreshToken() {
    if (this.connectorRefreshToken) {
      try {
        const tokens = await this.http
          .get<string>(`${this.baseUrl}RefreshToken`, {
            params: {
              token: this.connectorRefreshToken,
              connector: this.connector,
              region: this.region,
              accessListId: this.userService.user?.accessListId || '',
            },
          })
          .toPromise();
        if (tokens) {
          const refreshedTokens = JSON.parse(JSON.stringify(tokens));
          this.connectorToken = refreshedTokens.accessToken;
          this.connectorRefreshToken = refreshedTokens.refreshToken;
        }
      } catch (error) {
        throw new Error('ACCESS_TOKEN_REQUIRED');
      }
    }
  }

  private openLoginWindow(url: string) {
    // remove any left over listener
    window.removeEventListener('message', this.receiveLoginMessage, false);

    const height = 700;
    const width = 600;
    const left = screen.width / 2 - width / 2;
    const top = screen.height / 2 - height / 2;
    const screenX = window.screenX || 0;

    // window features
    const features = `toolbar=no, menubar=no, width=${width}, height=${height}, top=${top}, left=${left + screenX}`;

    // If new window just open
    if (this.loginWindow == null || this.loginWindow.closed) {
      this.loginWindow = window.open(url, 'cloud login', features);

      // If window changed, open and focus
    } else if (this.lastLoginUrl !== url) {
      this.loginWindow = window.open(url, 'cloud login', features);
      this.loginWindow?.focus();
    } else {
      // else just focus
      this.loginWindow?.focus();
    }

    // add listener for receiving tokens
    window.addEventListener('message', this.receiveLoginMessage, false);
    // retain last used login url
    this.lastLoginUrl = url;
  }

  private receiveLoginMessage = async (message: {
    data: {
      connectorProvider: string;
      access: string;
      region: string;
      connector: string;
      refresh: string;
    };
  }) => {
    if (message && message.data && message.data.connectorProvider === 'xacc') {
      window.removeEventListener('message', this.receiveLoginMessage, false);

      // if access is canceled user must have canceled so just remove listener and return
      if (message.data.access == 'canceled') {
        return;
      }

      this.region = message.data.region;
      this.connector = message.data.connector;
      this.connectorToken = message.data.access;
      this.connectorRefreshToken = message.data.refresh;

      if (this.connector === 'GraphOutlook') {
        // get user profile
        const profile = await this.getProfile();
        this.xaccLoginSucceed.emit({ profile: profile, repo: message.data });
        return;
      } else if (this.showBrowser === false) {
        this.xaccLoginSucceed.emit({ profile: null, repo: message.data });
        this.showBrowser = true; // reset
        return;
      }

      if (!this.repoOptions) {
        this.openBrowseModal(false);
      } else {
        try {
          await this.validatePath(this.repoOptions);
          this.repoOptions.connector = this.connector;
          this.repoOptions.region = this.region;
          this.repoOptions.access = this.connectorToken;
          this.repoOptions.refresh = this.connectorRefreshToken;
          this.directoryOrFilesSelected.emit(this.repoOptions);
        } catch {
          this.openBrowseModal(true);
        }
      }
    }
  };

  private openBrowseModal(returning: boolean) {
    const modalRef = this.modalService.open(XaccBrowseModalComponent, {
      windowClass: 'xrx-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.enableFileTypes = this.enableFileTypes;
    modalRef.componentInstance.multiSelect = this.isMultipleSelection;
    modalRef.componentInstance.maxSelection = this.maxSelection;
    modalRef.componentInstance.returning = returning;
    // uncomment out the following line to debug server side pagination.
    // modalRef.componentInstance.batchPageSize = 50;

    modalRef.result
      .then((repoOptions) => {
        this.repoOptions = repoOptions;
        this.directoryOrFilesSelected.emit(repoOptions);
      })
      .catch((repoOptions) => {
        this.directoryOrFilesSelected.emit(repoOptions);
      });
  }
}

export interface RepoItem {
  id: string;
  name: string;
  mimeType: string;
  childCount: number;
}

export interface RepoItemList {
  items: RepoItem[];
  count: number;
  nextPageToken?: string;
}

export interface MWItem {
  name: string;
  id?: string;
  Path: string;
  libraryId?: string;
  folderId?: boolean;
  isCollection?: boolean;
  readOnly?: boolean;
}

export interface MWCloudItem {
  id: string;
  name: string;
  type?: string;
}

export interface MWSelectedFilesInFolder {
  site: MWCloudItem;
  dir: MWCloudItem;
  files: MWCloudItem[];
}

export interface MWReturningRepo {
  connector: string;
  region: string;
  accessToken: string;
  refreshToken: string;
  sitePath: MWCloudItem[];
  dir: MWCloudItem;
  selectedFolders: MWSelectedFilesInFolder[];
}

export interface MWCloudRepo {
  connector: string;
  region?: string;
  sitePath?: MWCloudItem[];
  dir?: MWCloudItem;
  files?: MWCloudItem | MWSelectedFilesInFolder[];
  access?: string;
  refresh?: string;
  tenantId?: string;
}

export interface MSGraphProfile {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  mail: string;
}
